import { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { sxStyles, useStyles } from './Faxes.styles';
import {
  Heading,
  headingLevel,
  Table,
  Icons,
  colors,
  fontWeight,
  Menu,
  Text,
  textLevel,
} from '../../../packages';
import { BaseLayout } from '../../../layouts/base';
import { listFax, toggleFaxStatus } from '../../../services/report-service/reportService.service';
import useDebounce from '../../../hooks/useDebounce';

export const Faxes = () => {
  const navigate = useNavigate();
  const [tableParams, setTableParams] = useState({
    search: {
      searchKey: '',
    },
    pagination: {
      currentPage: 1,
      rowsPerPage: 10,
    },
    sorter: {
      column: 'date',
      direction: 'desc',
    },
  });

  const classes = useStyles();

  const [viewBy, setViewBy] = useState('not_reviewed');
  const [faxesData, setFaxesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [notReviewedCount, setNotReviewedCount] = useState(0);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [currentViewCount, setCurrentViewCount] = useState(0);

  const shouldFetchRef = useRef(false);

  const debouncedSearchTerm = useDebounce(tableParams.search.searchKey, 1000);

  const onChangeView = type => () => {
    setViewBy(type);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        currentPage: 1,
      },
    });
    shouldFetchRef.current = true;
    setCurrentViewCount(type === 'not_reviewed' ? notReviewedCount : reviewedCount);
  };

  const fetchFaxes = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const queryParams = {
        pageSize: tableParams.pagination.rowsPerPage,
        pageNumber: tableParams.pagination.currentPage,
        status: viewBy,
      };

      if (debouncedSearchTerm) {
        queryParams.search = debouncedSearchTerm;
      }

      const oppositeStatus = viewBy === 'not_reviewed' ? 'reviewed' : 'not_reviewed';

      const [response, countResponse] = await Promise.all([
        listFax(queryParams),
        listFax({
          pageSize: 1,
          pageNumber: 1,
          status: oppositeStatus,
        }),
      ]);

      if (response?.data?.data) {
        const faxesWithStatus = response.data.data.map(fax => ({
          ...fax,
          status: fax.status || 'not_reviewed',
          actions: {
            id: fax.jobId,
            status: fax.status,
          },
        }));
        setFaxesData(faxesWithStatus);

        const currentCount = response?.data?.metaData?.totalRecords || 0;
        const oppositeCount = countResponse?.data?.metaData?.totalRecords || 0;

        setCurrentViewCount(currentCount);

        if (viewBy === 'not_reviewed') {
          setNotReviewedCount(currentCount);
          setReviewedCount(oppositeCount);
        } else {
          setReviewedCount(currentCount);
          setNotReviewedCount(oppositeCount);
        }

        setTotalCount(currentCount + oppositeCount);
      } else {
        setFaxesData([]);
        setCurrentViewCount(0);
      }
    } catch (err) {
      setError('Failed to load faxes. Please try again later.');
      setFaxesData([]);
      setCurrentViewCount(0);
    } finally {
      setIsLoading(false);
      shouldFetchRef.current = false;
    }
  };

  useEffect(() => {
    if (shouldFetchRef.current || debouncedSearchTerm !== tableParams.search.searchKey) {
      void fetchFaxes();
    }
  }, [tableParams.pagination.currentPage, tableParams.pagination.rowsPerPage, viewBy, debouncedSearchTerm]);

  useEffect(() => {
    shouldFetchRef.current = true;
    void fetchFaxes();
  }, []);

  useEffect(() => {
    if (debouncedSearchTerm !== tableParams.search.searchKey) {
      setTableParams(prev => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          currentPage: 1,
        },
      }));
      shouldFetchRef.current = true;
    }
  }, [debouncedSearchTerm]);

  const handleRowClick = row => {
    if (row && row.jobId) {
      navigate(`/admin/fax/${row.jobId}`);
    }
  };

  const handleToggle = async id => {
    try {
      await toggleFaxStatus(id);
      shouldFetchRef.current = true;
      void fetchFaxes();
    } catch (error) {
      console.error('Error toggling fax status:', error);
    }
  };

  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(2);
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const formattedHours = hours % 12 || 12;

    return {
      date: `${month}/${day}/${year}`,
      time: `${formattedHours}:${minutes}${ampm}`,
    };
  };

  const renderColumns = [
    {
      id: 'status',
      label: 'Status',
      textAlign: 'center',
      width: '60px',
      renderCell: status => {
        return status === 'not_reviewed' ? (
          <Box sx={sxStyles.statusNotReviewed}>
            <Icons glyph="close-circle-outlined" color={colors.destructive500} size={16} />
          </Box>
        ) : (
          <Box sx={sxStyles.statusReviewed}>
            <Icons glyph="checked-circle-outlined" color={colors.success} size={16} />
          </Box>
        );
      },
    },
    {
      id: 'fromNumber',
      label: 'From Number',
      textAlign: 'center',
    },
    {
      id: 'toNumber',
      label: 'To Number',
      textAlign: 'center',
    },
    {
      id: 'faxTotalPages',
      label: 'Pages',
      textAlign: 'center',
    },
    {
      id: 'direction',
      label: 'Direction',
      textAlign: 'center',
    },
    {
      id: 'createdAt',
      label: 'Date',
      textAlign: 'center',
      renderCell: createdAt => {
        const { date, time } = formatDate(createdAt);
        return (
          <Box sx={sxStyles.dateContainer}>
            <Text sx={sxStyles.dateText} level={textLevel.S}>
              {date}
            </Text>
            <Text sx={sxStyles.timeText} level={textLevel.XS} color={colors.neutral500}>
              {time}
            </Text>
          </Box>
        );
      },
    },
    {
      id: 'actions',
      label: '',
      textAlign: 'right',
      width: '50px',
      renderCell: actionStatus => {
        const isReviewed = actionStatus.status === 'reviewed';

        return (
          <Menu
            icon="more"
            className={classes.menu}
            itemsWrapperClassName={classes.menuItemsWrapper}
            items={[
              {
                label: isReviewed ? 'Mark as Not Reviewed' : 'Mark as Reviewed',
                onClick: e => {
                  handleToggle(actionStatus.id);
                },
              },
            ]}
          />
        );
      },
    },
  ];

  const handleTableParamsChange = newParams => {
    setTableParams(newParams);
    shouldFetchRef.current = true;
  };

  return (
    <BaseLayout>
      <Box sx={sxStyles.pageWrapper}>
        <Box sx={sxStyles.container}>
          <Box sx={sxStyles.header}>
            <Box sx={sxStyles.flex}>
              <Heading className={classes.title} level={headingLevel.XL} weight={fontWeight.BOLD}>
                Faxes
              </Heading>
              <Box component="span" sx={sxStyles.totalCount}>
                {totalCount} total
              </Box>
            </Box>
          </Box>

          <Box sx={sxStyles.tableContainer}>
            <Box sx={sxStyles.customFiltersContainer}>
              <Box sx={sxStyles.filterTabsWrapper}>
                <Box
                  sx={{
                    ...sxStyles.notReviewedTab,
                    ...(viewBy === 'not_reviewed' ? sxStyles.activeTab : sxStyles.notActiveTab),
                  }}
                  onClick={onChangeView('not_reviewed')}
                >
                  <Icons glyph="close-circle-outlined" color={colors.destructive500} size={24} />
                  <span>Not reviewed</span>
                  <Box
                    sx={{
                      ...sxStyles.count,
                      ...(viewBy === 'not_reviewed' ? sxStyles.activeCount : sxStyles.notActiveCount),
                    }}
                  >
                    {notReviewedCount}
                  </Box>
                </Box>
                <Box
                  sx={{
                    ...sxStyles.reviewedTab,
                    ...(viewBy === 'reviewed' ? sxStyles.activeTab : sxStyles.notActiveTab),
                  }}
                  onClick={onChangeView('reviewed')}
                >
                  <Icons glyph="checked-circle-outlined" color={colors.success} size={16} />
                  <span>Reviewed</span>
                  <Box
                    sx={{
                      ...sxStyles.count,
                      ...(viewBy === 'reviewed' ? sxStyles.activeCount : sxStyles.notActiveCount),
                    }}
                  >
                    {reviewedCount}
                  </Box>
                </Box>
              </Box>
            </Box>
            <Table
              searchProps={{
                placeholder: 'Search faxes by from number',
              }}
              gridProps={{
                columns: renderColumns,
                data: error ? ['No Record Found'] : faxesData,
                isLoading: isLoading,
                onRowClick: handleRowClick,
                rowStyle: { cursor: 'pointer' },
              }}
              paginationProps={{
                currentRows: error ? 0 : faxesData.length,
                totalCount: error ? 0 : currentViewCount,
                showRowsPerPage: true,
              }}
              className={classes.tableCell}
              value={tableParams}
              onChange={handleTableParamsChange}
            />
          </Box>
        </Box>
      </Box>
    </BaseLayout>
  );
};

export default Faxes;
